/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayerSelectButtonConnect,
  LayerSelectConnect,
} from '@opengeoweb/layer-select';
import type { layerTypes, CoreAppStore, serviceTypes } from '@opengeoweb/store';
import {
  mapActions,
  mapEnums,
  uiActions,
  uiSelectors,
  uiTypes,
  mapSelectors,
  useSetupDialog,
} from '@opengeoweb/store';
import LayerManager from './LayerManager';
import { sizeLarge } from './LayerManagerHeaderOptions';
import { returnCorrectSize } from './LayerManagerUtils';
import { useFetchServices } from './useFetchServices';
import { useCoreTranslation } from '../../utils/i18n';
import { LayerInfoDialogConnect } from '../LayerInfoConnect';

export const getDialogType = (
  mapId: string,
  isMultiMap: boolean,
  isDocked: boolean,
): uiTypes.DialogType => {
  if (isDocked) {
    return `${uiTypes.DialogTypes.DockedLayerManager}-${mapId}`;
  }
  if (isMultiMap) {
    return `${uiTypes.DialogTypes.LayerManager}-${mapId}`;
  }
  return uiTypes.DialogTypes.LayerManager;
};

interface LayerManagerConnectProps {
  mapId?: string;
  preloadedAvailableBaseLayers?: layerTypes.Layer[];
  preloadedMapServices?: serviceTypes.InitialService[];
  preloadedBaseServices?: serviceTypes.InitialService[];
  bounds?: string;
  title?: string;
  showMapIdInTitle?: boolean;
  isMultiMap?: boolean;
  isDocked?: boolean;
  source?: uiTypes.Source;
}

/**
 * Layer Managerconnected to the store displaying the layers forthe active map Id
 * Please note that in order to use this and open/close the dialog, every map on the screen that should be able to show
 * a dialog should have the <LayerManagerMapButtonConnect/> which is used to focus the correct map and open the dialog
 *
 * Expects the following props:
 * @param {serviceTypes.InitialService[]} preloadedMapServices preloadedMapServices: array of Service objects - contains an array of Service objects that are preloaded into in the WMS Loader for maplayers
 * @param {serviceTypes.InitialService[]} preloadedBaseServices preloadedBaseServices: array of Service objects - contains an array of Service objects that are preloaded into in the WMS Loader fpr baselayers
 * @param {Layer[]} preloadedAvailableBaseLayers preloadedAvailableBaseLayers: array of Layer objects - contains an array of Layer objects (consisting solely of type baseLayer) that are available to be shown as basemaps
 * ``` <LayerManagerConnect />```
 */
const LayerManagerConnect: React.FC<LayerManagerConnectProps> = ({
  preloadedAvailableBaseLayers,
  preloadedMapServices,
  preloadedBaseServices,
  bounds,
  title,
  showMapIdInTitle = false,
  mapId: initialMapId = null!,
  isMultiMap = false,
  isDocked = false,
  source = 'app',
}: LayerManagerConnectProps) => {
  const dispatch = useDispatch();
  const dialogType = getDialogType(initialMapId, isMultiMap, isDocked);

  const { t } = useCoreTranslation();
  const nonEmptyTitle = title ?? t('layermanager-title');

  const activeMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, dialogType),
  );

  // In case of a docked layer manager or multimap, use the map id that is passed
  // For floating layer managers, use the currently active, selected mapid
  const mapId = initialMapId || activeMapId;

  const dockedLayerManagerSize = useSelector((store: CoreAppStore) =>
    mapSelectors.getDockedLayerManagerSize(store, mapId),
  );
  const dockedLayerManagerPosition = useSelector((store: CoreAppStore) =>
    mapSelectors.getDockedLayerManagerPosition(store, mapId),
  );
  const dockedLayerManagerDimensions = useSelector((store: CoreAppStore) =>
    mapSelectors.getDockedLayerManagerDimensions(store, mapId),
  );

  const {
    dialogOrder,
    setDialogOrder,
    onCloseDialog,
    isDialogOpen,
    uiSource,
    uiIsLoading,
    uiError,
  } = useSetupDialog(dialogType, source);

  // Only fetch services for floating layer manager, not for docked!
  useFetchServices(dialogType, preloadedMapServices, isDocked);

  const onToggleDock = React.useCallback((): void => {
    onCloseDialog();
    if (isDocked) {
      // Close docked layer manager and open the floating layer manager
      dispatch(
        uiActions.setActiveMapIdForDialog({
          type: uiTypes.DialogTypes.LayerManager,
          mapId,
          setOpen: true,
          source,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    } else {
      // Close the floating layer manager and open docked layer manager
      dispatch(
        uiActions.setToggleOpenDialog({
          type: `${uiTypes.DialogTypes.DockedLayerManager}-${mapId}`,
          setOpen: true,
          mapId,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    }
  }, [dispatch, isDocked, mapId, onCloseDialog, source]);

  return React.useMemo(() => {
    const shownTitle = showMapIdInTitle
      ? `${nonEmptyTitle} ${mapId}`
      : nonEmptyTitle;
    return (
      <>
        <LayerManager
          addLayerComponent={
            <LayerSelectButtonConnect
              isMultiMap={isMultiMap}
              mapId={mapId}
              source={uiSource}
            />
          }
          bounds={bounds}
          error={uiError}
          headerSize="xs"
          isDockedLayerManager={isDocked}
          isLoading={uiIsLoading}
          isOpen={isDialogOpen}
          mapId={mapId || initialMapId}
          onClose={onCloseDialog}
          onDockedLayerManagerDimensionsChange={(dimensions): void => {
            if (!isDocked) {
              return;
            }
            dispatch(
              mapActions.setDockedLayerManagerDimensions({
                mapId,
                dockedLayerManagerDimensions: dimensions,
                origin: mapEnums.MapActionOrigin.map,
              }),
            );
          }}
          onDockedLayerManagerDragEnd={(position): void => {
            if (!isDocked) {
              return;
            }
            dispatch(
              mapActions.setDockedLayerManagerPosition({
                mapId,
                dockedLayerManagerPosition: {
                  x: position.x,
                  y: position.y,
                },
                origin: mapEnums.MapActionOrigin.map,
              }),
            );
          }}
          onDockedLayerManagerSizeChange={(size): void => {
            if (!isDocked) {
              return;
            }
            dispatch(
              mapActions.setDockedLayerManagerSize({
                mapId,
                dockedLayerManagerSize: size,
                origin: mapEnums.MapActionOrigin.map,
              }),
            );
          }}
          onMouseDown={setDialogOrder}
          onToggleDock={onToggleDock}
          order={dialogOrder}
          preloadedAvailableBaseLayers={preloadedAvailableBaseLayers}
          preloadedBaseServices={preloadedBaseServices}
          size={
            isDocked
              ? dockedLayerManagerDimensions ||
                returnCorrectSize(dockedLayerManagerSize)
              : sizeLarge
          }
          source={uiSource}
          startDraggablePosition={
            isDocked ? dockedLayerManagerPosition : { x: 0, y: 0 }
          }
          startPosition={
            isDocked ? { top: 60, right: 16 } : { top: 85, left: 50 }
          }
          title={shownTitle}
        />

        {!isDocked && (
          <LayerInfoDialogConnect mapId={mapId} source={uiSource} />
        )}
        {!isDocked && <LayerSelectConnect />}
      </>
    );
  }, [
    bounds,
    dialogOrder,
    dispatch,
    dockedLayerManagerPosition,
    dockedLayerManagerDimensions,
    dockedLayerManagerSize,
    initialMapId,
    isDialogOpen,
    isDocked,
    isMultiMap,
    mapId,
    onCloseDialog,
    onToggleDock,
    preloadedAvailableBaseLayers,
    preloadedBaseServices,
    setDialogOrder,
    showMapIdInTitle,
    nonEmptyTitle,
    uiError,
    uiIsLoading,
    uiSource,
  ]);
};

export default LayerManagerConnect;
